/**
 * Conference Workspace — where the barrister actually ADVISES.
 *
 * A conference isn't an appointment; it's the moment counsel gives advice, and its
 * consequences live on the matter for weeks. This workspace pulls together what the
 * barrister needs to advise (context + papers), captures the advice/outcome, and
 * turns the outcome into matter progress (follow-up tasks, fee, next hearing) —
 * reusing the documents, task engine and timeline already built, not a separate app.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  X, FileText, ChatCircleDots, CheckCircle, ListPlus, CurrencyGbp, Gavel, Eye, CircleNotch, Users,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getApiErrorMessage } from '@/lib/api';
import { fetchDocuments, type DocFile } from '@/lib/documentsApi';
import { completeConference, PURPOSE_LABELS, FORMAT_LABELS, type Conference } from '@/lib/conferencesApi';
import { TaskModal } from '@/components/tasks/TaskModal';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { UploadDocumentModal } from '@/components/shared/UploadDocumentModal';
import { EnvelopeSimple, FilePlus } from '@phosphor-icons/react';

// The papers a barrister reaches for to advise, grouped the way they think.
const PAPER_GROUPS: Array<{ label: string; cats: string[] }> = [
  { label: 'Brief & instructions', cats: ['brief', 'pleading'] },
  { label: 'Skeleton & authorities', cats: ['skeleton', 'authorities'] },
  { label: 'Correspondence', cats: ['correspondence'] },
  { label: 'Orders & evidence', cats: ['order', 'judgment', 'evidence', 'bundle'] },
];

export function ConferenceWorkspace({
  conference, basePath, onClose, onChanged,
}: {
  conference: Conference;
  basePath: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const caseId = conference.caseId;
  const isHeld = conference.status === 'completed';

  const [advice, setAdvice] = useState(conference.outcome ?? '');
  const [taskOpen, setTaskOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<DocFile | null>(null);
  const [finishing, setFinishing] = useState(false);

  const { data: docs = [] } = useQuery({
    queryKey: ['conference-papers', caseId],
    queryFn: () => fetchDocuments(caseId),
  });

  const grouped = useMemo(() => {
    return PAPER_GROUPS.map((g) => ({
      ...g,
      docs: docs.filter((d) => g.cats.includes((d.category ?? '').toLowerCase())),
    })).filter((g) => g.docs.length > 0);
  }, [docs]);

  const when = conference.scheduledAt ? new Date(conference.scheduledAt) : null;

  const finish = async () => {
    setFinishing(true);
    try {
      await completeConference(conference.id, advice.trim() || undefined);
      toast.success('Advice recorded — matter timeline updated');
      qc.invalidateQueries({ queryKey: ['matter-conferences', caseId] });
      qc.invalidateQueries({ queryKey: ['matter-fee-records', caseId] });
      onChanged();
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not record the outcome'));
      setFinishing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="flex w-full max-w-2xl flex-col overflow-y-auto bg-card shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-border bg-card px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <ChatCircleDots className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              <h2 className="text-base font-semibold text-foreground">{PURPOSE_LABELS[conference.purpose]} conference</h2>
              <Badge variant={isHeld ? 'default' : 'success'}>{isHeld ? 'Completed' : 'Ready to advise'}</Badge>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {conference.case.caseNumber} · {conference.case.caseName}
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 space-y-6 px-6 py-5">
          {/* Context — everything to walk in prepared */}
          <section>
            <SectionTitle icon={<Users className="h-3.5 w-3.5" />} title="Context" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md border border-border bg-muted/20 p-3 text-xs">
              <KV k="When" v={when ? when.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'TBC'} />
              <KV k="Format" v={FORMAT_LABELS[conference.format]} />
              <KV k="Duration" v={`${conference.durationMinutes} min`} />
              <KV k="Where" v={conference.locationDetails ?? '—'} />
              <KV k="Lay client" v={conference.layClientAttending ? 'Attending' : 'Not attending'} />
              <KV k="Requested by" v={`${conference.requestedBy.firstName} ${conference.requestedBy.lastName}`} />
            </div>
            {conference.notes && <p className="mt-2 text-xs text-muted-foreground"><span className="font-medium text-foreground">Objective:</span> {conference.notes}</p>}
          </section>

          {/* Papers — reuse the matter's real documents */}
          <section>
            <SectionTitle icon={<FileText className="h-3.5 w-3.5" />} title="Papers" />
            {grouped.length === 0 ? (
              <p className="text-xs text-muted-foreground">No categorised papers on this matter yet.</p>
            ) : (
              <div className="space-y-3">
                {grouped.map((g) => (
                  <div key={g.label}>
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{g.label}</div>
                    <div className="space-y-1">
                      {g.docs.map((d) => (
                        <button key={d.id} onClick={() => setPreviewDoc(d)} className="flex w-full items-center gap-2 rounded-md border border-border/60 px-2.5 py-1.5 text-left text-xs hover:bg-muted/40">
                          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="flex-1 truncate text-foreground">{d.name}</span>
                          <Eye className="h-3.5 w-3.5 shrink-0 text-brand-600 dark:text-brand-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => { onClose(); navigate(`${basePath}/matters/${caseId}?tab=documents`); }} className="mt-2 text-xs text-brand-600 dark:text-brand-400 hover:underline">
              Open all documents →
            </button>
          </section>

          {/* Advice & outcome — the professional core */}
          <section>
            <SectionTitle icon={<ChatCircleDots className="h-3.5 w-3.5" />} title="Advice &amp; outcome" />
            {isHeld ? (
              <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/20 p-3 text-sm text-foreground">{conference.outcome || 'No outcome recorded.'}</div>
            ) : (
              <textarea
                value={advice}
                onChange={(e) => setAdvice(e.target.value)}
                rows={6}
                placeholder="What was discussed, the advice given, decisions and instructions taken, and any follow-up promised…"
                className="w-full resize-none rounded-md border border-border bg-card p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/40"
              />
            )}
          </section>

          {/* Follow-ups — turn the advice into matter progress (reuse tasks/fees/hearings) */}
          <section>
            <SectionTitle icon={<ListPlus className="h-3.5 w-3.5" />} title="Turn it into action" />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" leftIcon={<ListPlus className="h-3.5 w-3.5" />} onClick={() => setTaskOpen(true)}>Follow-up task</Button>
              <Button variant="outline" size="sm" leftIcon={<FilePlus className="h-3.5 w-3.5" />} onClick={() => setUploadOpen(true)}>Add a document</Button>
              <Button variant="outline" size="sm" leftIcon={<EnvelopeSimple className="h-3.5 w-3.5" />} onClick={() => { onClose(); navigate(`${basePath}/matters/${caseId}?tab=messages`); }}>Follow-up email</Button>
              <Button variant="outline" size="sm" leftIcon={<Gavel className="h-3.5 w-3.5" />} onClick={() => { onClose(); navigate(`${basePath}/matters/${caseId}?tab=hearings`); }}>Need a hearing</Button>
              <Button variant="outline" size="sm" leftIcon={<CurrencyGbp className="h-3.5 w-3.5" />} onClick={() => { onClose(); navigate(`${basePath}/matters/${caseId}?tab=fees`); }}>Fee note</Button>
            </div>
          </section>
        </div>

        {/* Footer */}
        {!isHeld && (
          <div className="sticky bottom-0 border-t border-border bg-card px-6 py-4">
            <Button variant="primary" size="md" className="w-full" disabled={finishing} leftIcon={finishing ? <CircleNotch className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} onClick={finish}>
              Record advice &amp; finish conference
            </Button>
            <p className="mt-1.5 text-center text-xs text-muted-foreground">Writes the outcome to the matter timeline and drafts the conference fee.</p>
          </div>
        )}
      </div>

      {taskOpen && (
        <TaskModal
          matterId={caseId}
          onClose={() => setTaskOpen(false)}
          onSaved={() => { setTaskOpen(false); toast.success('Follow-up task added'); qc.invalidateQueries({ queryKey: ['matter-tasks', caseId] }); }}
        />
      )}
      {previewDoc && (
        <DocumentPreviewModal documentId={previewDoc.id} documentName={previewDoc.name} onClose={() => setPreviewDoc(null)} />
      )}
      <UploadDocumentModal
        open={uploadOpen}
        defaultCaseId={caseId}
        onClose={() => setUploadOpen(false)}
        onUpload={() => { setUploadOpen(false); toast.success('Document added to the matter'); qc.invalidateQueries({ queryKey: ['conference-papers', caseId] }); }}
      />
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
      {icon}{title}
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right font-medium text-foreground">{v}</span>
    </div>
  );
}
